import client from './client';
import {
    Company,
    CursorResult,
    Opportunity,
    PageResult,
    Person,
    PlanItem,
    ProjectV2,
    WorkTask,
    WorkbenchAction,
} from '../../models/v2';
import {
    BatchRecipient,
    EmailTemplateImageAsset,
    EmailTemplatePreview,
    EmailTemplateReference,
    EmailTestRecipient,
    EmailTestSendResult,
    InboundMailMessage,
    MailBatch,
    MailDispatchEvent,
    MailDispatchOperationsStatus,
    MailboxMonitor,
    MailAccount,
    RecipientImportRow,
    RecipientImportSummary,
    RecipientListMember,
    RecipientListOwner,
    RecipientListVersion,
    RecipientListReference,
} from '../../models/mail';

export const v2Api = {
    workbench: async (params?: { cursor?: string; limit?: number }) => {
        const result = await client.get<CursorResult<WorkbenchAction>>(
            '/workbench/actions',
            params
        );
        const urgency = [
            'OVERDUE',
            'TODAY',
            'CUSTOMER_WAITING',
            'THREE_DAYS',
            'SEVEN_DAYS',
            'NO_DATE',
        ];
        return {
            ...result,
            items: (result.items || []).map((item) => ({
                ...item,
                urgencyGroup: item.urgencyGroup || urgency[item.sortGroup],
            })),
        };
    },
    companies: (params?: any) => client.get<PageResult<Company>>('/companies', params),
    company: (id: number) => client.get<Company>(`/companies/${id}`),
    createCompany: (body: any) => client.post<{ id: number }>('/companies', body),
    patchCompany: (id: number, body: any) => client.patch<Company>(`/companies/${id}`, body),
    persons: (params?: any) => client.get<PageResult<Person>>('/persons', params),
    person: (id: number) => client.get<Person>(`/persons/${id}`),
    createPerson: (body: any) => client.post<{ id: number }>('/persons', body),
    changeEmployment: (id: number, body: any) =>
        client.post<{ id: number }>(`/persons/${id}/employments/change`, body),
    tasks: (params?: any) => client.get<PageResult<WorkTask>>('/tasks', params),
    createTask: (body: any) => client.post<{ id: number }>('/tasks', body),
    transitionTask: (id: number, action: string, body: any) =>
        client.post<WorkTask>(`/tasks/${id}/${action}`, body),
    assignTask: (id: number, action: string, body: any) =>
        client.post<WorkTask>(`/tasks/${id}/${action}`, body),
    communications: (params?: any) => client.get<PageResult<any>>('/communications', params),
    createCommunication: (body: any) => client.post<{ id: number }>('/communications', body),
    signals: (params?: any) => client.get<PageResult<any>>('/signals', params),
    triageSignal: (id: number, body: any) => client.post<any>(`/signals/${id}/triage`, body),
    opportunities: (params?: any) => client.get<PageResult<Opportunity>>('/opportunities', params),
    opportunity: (id: number) => client.get<Opportunity>(`/opportunities/${id}`),
    createOpportunity: (body: any) => client.post<{ id: number }>('/opportunities', body),
    transitionOpportunity: (id: number, body: any) =>
        client.post<Opportunity>(`/opportunities/${id}/stage-transitions`, body),
    quotes: (id: number) => client.get<any[]>(`/opportunities/${id}/quotes`),
    projects: (params?: any) => client.get<PageResult<ProjectV2>>('/projects', params),
    project: (id: number) => client.get<ProjectV2>(`/projects/${id}`),
    createProject: (body: any) => client.post<{ id: number }>('/projects', body),
    patchProject: (id: number, body: any) => client.patch<ProjectV2>(`/projects/${id}`, body),
    transitionProject: (id: number, body: any) =>
        client.post<ProjectV2>(`/projects/${id}/status-transitions`, body),
    planItems: (id: number) => client.get<PlanItem[]>(`/projects/${id}/plan-items`),
    addPlanItem: (id: number, body: any) =>
        client.post<{ id: number }>(`/projects/${id}/plan-items`, body),
    patchPlanItem: (id: number, itemId: number, body: any) =>
        client.patch<any>(`/projects/${id}/plan-items/${itemId}`, body),
    paymentNodes: (id: number) => client.get<any[]>(`/projects/${id}/payment-nodes`),
    addPaymentNode: (id: number, body: any) =>
        client.post<{ id: number }>(`/projects/${id}/payment-nodes`, body),
    analytics: (topic: string, params: any) => client.get<any[]>(`/analytics/${topic}`, params),
    auditEvents: (params?: any) => client.get<PageResult<any>>('/audit-events', params),
    search: (keyword: string, limit = 20) => client.get<any[]>('/search', { keyword, limit }),

    createEmailContent: (body: {
        contentName: string;
        businessDomainCode?: string;
        ownerId: number;
    }) => client.post<{ id: number }>('/email-contents', body),
    emailContents: (params?: { ownerId?: number; keyword?: string; pageNo?: number; pageSize?: number }) =>
        client.get<PageResult<EmailTemplateReference>>('/email-contents', params),
    availableEmailContentOptions: (ownerId?: number) =>
        client.get<EmailTemplateReference[]>('/email-contents/options/available', { ownerId }),
    updateEmailContent: (
        contentId: number,
        body: {
            contentName: string;
            businessDomainCode?: string;
            ownerId: number;
            version: number;
        }
    ) => client.patch<EmailTemplateReference>(`/email-contents/${contentId}`, body),
    archiveEmailContent: (contentId: number, body: { version: number; reason: string }) =>
        client.delete<EmailTemplateReference>(`/email-contents/${contentId}`, body),
    importEmailHtml: (
        contentId: number,
        file: File,
        metadata: {
            subjectText: string;
            previewText?: string;
            senderDisplayName: string;
            replyToAddress: string;
            validUntil?: string;
        }
    ) => {
        const form = new FormData();
        form.append('file', file);
        form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
        return client.postMultipart<EmailTemplateReference>(
            `/email-contents/${contentId}/versions/import-html`,
            form
        );
    },
    createEmailContentVersion: (
        contentId: number,
        body: {
            htmlBody: string;
            subjectText: string;
            previewText?: string;
            senderDisplayName: string;
            replyToAddress: string;
            validUntil?: string;
        }
    ) => client.post<EmailTemplateReference>(`/email-contents/${contentId}/versions`, body),
    uploadEmailTemplateImage: (file: File) => {
        const form = new FormData();
        form.append('file', file);
        return client.postMultipart<EmailTemplateImageAsset>(
            '/email-contents/assets/images',
            form
        );
    },
    copyEmailContentVersion: (
        contentId: number,
        versionId: number,
        body: {
            subjectText: string;
            previewText?: string;
            senderDisplayName: string;
            replyToAddress: string;
            validUntil?: string;
        }
    ) => client.post<EmailTemplateReference>(
        `/email-contents/${contentId}/versions/${versionId}/copy`,
        body
    ),
    emailContentPreview: (contentId: number, versionId: number, mode = 'desktop') =>
        client.get<EmailTemplatePreview>(
            `/email-contents/${contentId}/versions/${versionId}/preview`,
            { mode }
        ),
    emailTestRecipients: () =>
        client.get<EmailTestRecipient[]>('/email-contents/test-recipients'),
    addEmailTestRecipients: (recipients: Array<{ email: string }>) =>
        client.post<EmailTestRecipient[]>('/email-contents/test-recipients', { recipients }),
    removeEmailTestRecipient: (recipientId: number) =>
        client.delete<{ id: number }>(`/email-contents/test-recipients/${recipientId}`),
    sendEmailTemplateTest: (
        contentId: number,
        versionId: number,
        body: { mailAccountId: number; recipientIds: number[] }
    ) => client.postLong<EmailTestSendResult>(
        `/email-contents/${contentId}/versions/${versionId}/test-send`,
        body
    ),

    mailAccounts: (params?: any) => client.get<PageResult<MailAccount>>('/mail-accounts', params),
    activeMailAccountOptions: () => client.get<MailAccount[]>('/mail-accounts/options'),
    createMailAccount: (body: any) => client.post<{ id: number }>('/mail-accounts', body),
    updateMailAccount: (id: number, body: any) =>
        client.patch<MailAccount>(`/mail-accounts/${id}`, body),
    testMailAccountOutbound: (id: number) =>
        client.post<{ result: string; message: string }>(`/mail-accounts/${id}/test-outbound`, {}),
    verifyAndActivateMailAccount: (id: number) =>
        client.post<MailAccount>(`/mail-accounts/${id}/verify-and-activate`, {}),

    createRecipientList: (body: {
        listName: string;
        description?: string;
        businessDomainCode?: string;
        ownerId: number;
        sourceType: 'MANUAL';
    }) => client.post<RecipientListReference>('/recipient-lists', body),
    recipientLists: (params?: any) =>
        client.get<PageResult<RecipientListReference>>('/recipient-lists', params),
    recipientListOwners: () =>
        client.get<{ items: RecipientListOwner[]; canViewAll: boolean; currentActorId: number }>(
            '/recipient-lists/owners'
        ),
    frozenRecipientListOptions: (ownerId?: number) =>
        client.get<RecipientListReference[]>('/recipient-lists/options/frozen', { ownerId }),
    recipientListVersions: (listId: number) =>
        client.get<RecipientListVersion[]>(`/recipient-lists/${listId}/versions`),
    createRecipientListVersion: (
        listId: number,
        body: { baseVersionId: number; reason: string }
    ) => client.post<RecipientListReference>(`/recipient-lists/${listId}/versions`, body),
    recipientList: (listId: number, versionId: number) =>
        client.get<RecipientListReference>(`/recipient-lists/${listId}/versions/${versionId}`),
    recipientListMembers: (listId: number, versionId: number, params?: any) =>
        client.get<PageResult<RecipientListMember>>(
            `/recipient-lists/${listId}/versions/${versionId}/members`,
            params
        ),
    addRecipientListMembers: (
        listId: number,
        versionId: number,
        members: Array<{
            companyId: number;
            personId: number;
            employmentId: number;
            contactMethodId: number;
        }>
    ) =>
        client.post<any>(`/recipient-lists/${listId}/versions/${versionId}/members`, {
            members,
        }),
    addRecipientListLegacyCustomers: (
        listId: number,
        versionId: number,
        customerIds: number[]
    ) =>
        client.post<any>(
            `/recipient-lists/${listId}/versions/${versionId}/members/from-legacy-customers`,
            { customerIds }
        ),
    updateRecipientListMember: (
        listId: number,
        versionId: number,
        memberId: number,
        body: { eligibilityStatus: 'ELIGIBLE' | 'EXCLUDED'; exclusionDetail?: string; reason: string }
    ) => client.patch<any>(`/recipient-lists/${listId}/versions/${versionId}/members/${memberId}`, body),
    removeRecipientListMember: (
        listId: number,
        versionId: number,
        memberId: number,
        reason: string
    ) => client.delete<any>(`/recipient-lists/${listId}/versions/${versionId}/members/${memberId}`, { reason }),
    freezeRecipientList: (
        listId: number,
        versionId: number,
        body: { contentHash: string; reason: string }
    ) =>
        client.post<RecipientListReference>(
            `/recipient-lists/${listId}/versions/${versionId}/freeze`,
            body
        ),
    unfreezeRecipientList: (listId: number, versionId: number, reason: string) =>
        client.post<RecipientListReference>(
            `/recipient-lists/${listId}/versions/${versionId}/unfreeze`,
            { reason }
        ),
    importRecipientEmails: (listId: number, versionId: number, file: File) => {
        const form = new FormData();
        form.append('file', file);
        return client.postMultipart<RecipientImportSummary>(
            `/recipient-lists/${listId}/versions/${versionId}/imports`,
            form,
            true
        );
    },
    recipientImport: (listId: number, versionId: number, importId: number) =>
        client.get<RecipientImportSummary>(
            `/recipient-lists/${listId}/versions/${versionId}/imports/${importId}`
        ),
    recipientImportRows: (listId: number, versionId: number, importId: number, params?: any) =>
        client.get<PageResult<RecipientImportRow>>(
            `/recipient-lists/${listId}/versions/${versionId}/imports/${importId}/rows`,
            params
        ),
    resolveRecipientImportRow: (
        listId: number,
        versionId: number,
        importId: number,
        rowId: number,
        body: any
    ) =>
        client.post<RecipientImportRow>(
            `/recipient-lists/${listId}/versions/${versionId}/imports/${importId}/rows/${rowId}/resolve`,
            body
        ),
    commitRecipientImport: (listId: number, versionId: number, importId: number, body: any) =>
        client.post<any>(
            `/recipient-lists/${listId}/versions/${versionId}/imports/${importId}/commit`,
            body
        ),

    mailBatches: (params?: any) => client.get<PageResult<MailBatch>>('/send-batches', params),
    mailBatch: (id: number) => client.get<MailBatch>(`/send-batches/${id}`),
    mailBatchRecipients: (id: number, params?: any) =>
        client.get<PageResult<BatchRecipient>>(`/send-batches/${id}/recipients`, params),
    createMailBatch: (body: any) => client.post<{ id: number }>('/send-batches', body),
    precheckMailBatch: (id: number, body: { version: number; reason: string }) =>
        client.post<any>(`/send-batches/${id}/precheck`, body),
    confirmMailBatch: (
        id: number,
        body: { version: number; precheckHash: string; reason: string }
    ) => client.post<MailBatch>(`/send-batches/${id}/confirm`, body),
    controlMailBatch: (id: number, action: 'pause' | 'resume' | 'cancel', body: any) =>
        client.post<MailBatch>(`/send-batches/${id}/${action}`, body),
    retryMailRecipient: (batchId: number, recipientId: number, body: any) =>
        client.post<{ childBatchId: number; newRecipientId: number }>(
            `/send-batches/${batchId}/recipients/${recipientId}/retry`,
            body
        ),
    reconcileUnknownMailRecipient: (
        batchId: number,
        recipientId: number,
        body: { version: number; resolution: 'CONFIRMED_NOT_SENT'; reason: string }
    ) =>
        client.post<{
            batchId: number;
            recipientId: number;
            sendStatus: string;
            batchStatus: string;
            version: number;
        }>(
            `/send-batches/${batchId}/recipients/${recipientId}/reconcile`,
            body
        ),
    reconcileUnknownMailRecipients: (
        batchId: number,
        body: {
            items: Array<{ recipientId: number; version: number }>;
            resolution: 'CONFIRMED_NOT_SENT';
            reason: string;
        }
    ) =>
        client.post<{ batchId: number; processedCount: number; batchStatus: string }>(
            `/send-batches/${batchId}/recipients/reconcile`,
            body
        ),
    mailDispatchOperationsStatus: () =>
        client.get<MailDispatchOperationsStatus>('/mail-operations/status'),
    mailDispatchEvents: (params?: any) =>
        client.get<PageResult<MailDispatchEvent>>('/mail-operations/events', params),

    unsubscribeState: (token: string) =>
        client.get<{ status: string; confirmationRequired: boolean }>(
            `/mail/unsubscribe/${encodeURIComponent(token)}`
        ),
    unsubscribe: (token: string) => {
        const form = new URLSearchParams();
        form.set('List-Unsubscribe', 'One-Click');
        return client.postForm<{ status: string; confirmationRequired: boolean }>(
            `/mail/unsubscribe/${encodeURIComponent(token)}`,
            form
        );
    },

    mailboxMonitor: (mailAccountId: number) =>
        client.get<MailboxMonitor>(`/mail-accounts/${mailAccountId}/inbound-monitor`),
    updateMailboxMonitor: (mailAccountId: number, body: Partial<MailboxMonitor>) =>
        client.patch<MailboxMonitor>(`/mail-accounts/${mailAccountId}/inbound-monitor`, body),
    runMailboxMonitor: (mailAccountId: number) =>
        client.post<any>(`/mail-accounts/${mailAccountId}/inbound-monitor/run`, {}),
    inboundMessages: (params?: any) =>
        client.get<PageResult<InboundMailMessage>>('/mail/inbound-messages', params),
    reconcileInboundMessage: (id: number, body: any) =>
        client.post<InboundMailMessage>(`/mail/inbound-messages/${id}/reconcile`, body),
};

export { V2ApiError } from './client';
